export interface AuditColumnModel {
  _id?: string;
  isDeleted?: boolean;
  metadata?: {
    modifiedBy: string;
    modifiedDate: string;
    createdBy: string;
    createdDate: string;
  };
}

export interface StatusModel {
  type: string;
  title: string;
  colorCode: string;
  backgroundColorCode: string;
}

export interface PaginationModel {
  currentPage: number;
  endIndex: number;
  endPage: boolean;
  pages: number[];
  pageCount: number;
  startIndex: number;
  totalItems: number;
}

export interface ResponseModel<T> {
  message?: string;
  data?: T;
  pageInfo?: PaginationModel;
  status?: boolean;
}

export interface ResponseModelV2<T> {
  response: {
    data: T;
    pageInfo?: PaginationModel;
    message?: string;
  };
  status: boolean;
  message?: string;
}

export interface ResponseModelV3<T> {
  response: {
    data: T;
  };
  pageInfo?: PaginationModel;
  status: boolean;
}

export interface ResponseModelV4<T> {
  response: T;
  pageInfo?: PaginationModel;
  status: boolean;
}

export interface StatusModel {
  type: string;
  title: string;
  colorCode: string;
  backgroundColorCode: string;
}

export enum Tab {
  HOST = 'Host',
  MODULES = 'Modules',
  PERIPHERALS = 'Peripherals',
}

export enum UtilisationLevel {
  OPTIMAL = 'optimal',
  WARNING = 'warning',
  CRITICAL = 'critical',
  UNKNOWN = 'unknown',
}

export enum UtilisationClass {
  OPTIMAL = 'level-optimal',
  WARNING = 'level-warning',
  CRITICAL = 'level-critical',
  UNKNOWN = 'level-unknown',
}

export enum ZoneNatureLevel {
  PRJ = 'Project',
  PHS = 'Phase',
  BLD = 'Building',
  FLR = 'Floors',
  REZ = 'Restricted Entry Zones',
  PTW = 'Permit To Work Zones',
}

export enum ZoneViewer {
  TREE_VIEW = 'Tree View',
  PLAN_VIEW = 'Plan View',
  PLANOGRAM_VIEW = 'Planogram View',
  ALL_SITES = 'All Sites',
}

export enum ActionMode {
  VIEW = 'view',
  EDIT = 'edit',
  RESTORE = 'restore',
  DELETE = 'delete',
  ADD = 'add',
  COPY = 'copy',
  UPLOAD = 'upload',
  DOWNLOAD = 'download',
  STATS = 'stats',
  ATTACH = 'attach',
  CLONE = 'clone',
  MAP = 'map',
  SUBMIT = 'submit',
  CLEAR = 'clear',
  CANCEL = 'cancel',
  TAG = 'tag',
  UNTAG = 'untag',
  DEFAULT = 'default',
  UPDATE = 'update',
  BACK = 'back',
  NEXT = 'next',
  TAGCAMERA = 'tagCamera',
  UNTAGCAMERA = 'untagCamera',
}

export enum Position {
  ABOVE = 'above',
  BELOW = 'below',
  LEFT = 'left',
  RIGHT = 'right',
  BEFORE = 'before',
  AFTER = 'after',
}

export enum Visibility {
  OPEN = 'open',
  CLOSE = 'close',
}

export enum FormField {
  TEXT_FIELD,
  DROP_DOWN,
  SLIDE_TOGGLE,
  SLIDE_TOGGLE_GROUP,
  TEXT_AREA,
  SEARCH_DROP_DOWN,
  MULTI_SELECT_DROP_DOWN,
  IMAGE_UPLOAD
}

export enum FormFieldInput {
  NUMBER,
  ALPHA_NUMERIC,
  TEXT,
}

export enum FormFieldOption {
  API,
}

export enum Status {
  ON = 'on',
  OFF = 'off',
  UNKNOWN = 'unknown',
  OPTIMAL = 'optimal',
  WARNING = 'warning',
  CRITICAL = 'critical',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum ViewMode {
  IN_PAGE = 'in_page',
  DIALOG = 'dialog',
  DRAWER = 'drawer',
}

export enum Scope {
  GLOBAL = 'Global',
  TENANT = 'Tenant',
}

export type DateType =
  | 'millisec'
  | 'sec'
  | 'min'
  | 'hour'
  | 'day'
  | 'month'
  | 'year';

export type ZoneNatureLevelType = keyof typeof ZoneNatureLevel;

export type ZoneViewerType =
  | ZoneViewer.TREE_VIEW
  | ZoneViewer.PLAN_VIEW
  | ZoneViewer.PLANOGRAM_VIEW
  | ZoneViewer.ALL_SITES;

export type ActionModeType =
  | ActionMode.ADD
  | ActionMode.ATTACH
  | ActionMode.CANCEL
  | ActionMode.CLEAR
  | ActionMode.CLONE
  | ActionMode.COPY
  | ActionMode.DELETE
  | ActionMode.DOWNLOAD
  | ActionMode.EDIT
  | ActionMode.MAP
  | ActionMode.RESTORE
  | ActionMode.STATS
  | ActionMode.SUBMIT
  | ActionMode.UPLOAD
  | ActionMode.VIEW
  | ActionMode.TAG
  | ActionMode.UNTAG
  | ActionMode.DEFAULT
  | ActionMode.UPDATE
  | ActionMode.BACK
  | ActionMode.NEXT
  | ActionMode.TAGCAMERA
  | ActionMode.UNTAGCAMERA;

export type PositionType =
  | Position.ABOVE
  | Position.AFTER
  | Position.BEFORE
  | Position.BELOW
  | Position.LEFT
  | Position.RIGHT;

export type VisibilityType = Visibility.CLOSE | Visibility.OPEN;

export type FormFieldType = keyof typeof FormField;

export type FormFieldInputType = keyof typeof FormFieldInput;

export type FormOptionType = keyof typeof FormFieldOption;

export type StatusType =
  | Status.ACTIVE
  | Status.CRITICAL
  | Status.INACTIVE
  | Status.OFF
  | Status.ON
  | Status.OPTIMAL
  | Status.UNKNOWN
  | Status.WARNING;

export type ViewModeType = ViewMode.DIALOG | ViewMode.DRAWER | ViewMode.IN_PAGE;

export type ScopeType = Scope.GLOBAL | Scope.TENANT;

export interface CoordinatesModel {
  lat: number | string;
  lng: number | string;
}

export interface DimensionModel {
  width: number;
  height: number;
}

export interface OffsetModel {
  left: number;
  top: number;
}

export interface MousePointModel {
  x: number;
  y: number;
}

export interface UnknownModel {
  [s: string]: any;
}

export interface TimeDifferenceResult {
  isInPast: boolean;
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  months: number;
  years: number;
  totalSeconds: number;
  totalMinutes: number;
  totalHours: number;
  totalDays: number;
  totalMonths: number;
  totalYears: number;
}
